import { useEffect, useState } from 'react';
import { Alert, App, Button, Empty, Input, Popconfirm, Segmented, Select, Switch } from 'antd';
import { DeleteOutlined, PlusOutlined, RollbackOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import JsonEditor from '@components/JsonEditor';
import {
  getJobCustomParameterTaskTypes, getJobCustomParametersConfig, resetJobCustomParametersConfig,
  updateJobCustomParametersConfig,
} from '@apis/jobCustomParameters';
import { type TaskType } from '@apis/simulation';
import {
  CUSTOM_PARAMETER_INPUT_TYPES, MAX_CUSTOM_PARAMETERS,
  definitionsToRows, definitionRowsToJson, parameterDefinitionRowsError, parameterDefinitionsJsonError,
  type CustomParameterDefinitions, type CustomParameterDefinitionRow,
} from '@/utils/jobCustomParameters';
import { Panel, PanelError, PanelLoading, RefreshButton, SOURCE_PILL, useConfigResource, useReportDirty } from './shared';
import './jobCustomParameters.less';

const CARD_ID = 'sec-job-custom-parameters';
// 本地行 ID 不随 key 编辑变化；否则每次输入字符都会重建控件并丢失焦点。
interface EditorRow extends CustomParameterDefinitionRow { rowId: number }
let nextRowId = 0;
const withRowIds = (rows: CustomParameterDefinitionRow[]): EditorRow[] => rows.map(row => ({ ...row, rowId: ++nextRowId }));
const withoutRowIds = (rows: EditorRow[]): CustomParameterDefinitionRow[] => rows.map(({ rowId: _, ...row }) => row);

export default function JobCustomParametersCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [view, setView] = useState<'form' | 'json'>('form');
  const [rows, setRows] = useState<EditorRow[]>([]);
  const [jsonDraft, setJsonDraft] = useState('{}');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [taskTypesLoading, setTaskTypesLoading] = useState(false);
  const [taskTypesError, setTaskTypesError] = useState(false);
  const [taskTypesRevision, setTaskTypesRevision] = useState(0);

  const loadDraft = (raw: string) => {
    setJsonDraft(raw);
    if (parameterDefinitionsJsonError(raw)) {
      setView('json');
      setRows([]);
    } else {
      setRows(withRowIds(definitionsToRows(JSON.parse(raw) as CustomParameterDefinitions)));
    }
  };
  const { loading, config, applyConfig, fetchConfig } = useConfigResource({
    id: CARD_ID, title: '任务自定义参数', fetcher: getJobCustomParametersConfig,
    onLoaded: cfg => loadDraft(cfg.currentValue),
  });

  useEffect(() => {
    let cancelled = false;
    setTaskTypesLoading(true);
    setTaskTypesError(false);
    getJobCustomParameterTaskTypes().then(types => { if (!cancelled) setTaskTypes(types); })
      .catch(() => { if (!cancelled) setTaskTypesError(true); })
      .finally(() => { if (!cancelled) setTaskTypesLoading(false); });
    return () => { cancelled = true; };
  }, [taskTypesRevision]);

  const formRows = withoutRowIds(rows);
  const formError = parameterDefinitionRowsError(formRows);
  const draft = view === 'form' ? definitionRowsToJson(formRows) : jsonDraft;
  const error = (view === 'form' ? formError : null) ?? parameterDefinitionsJsonError(draft);
  const dirty = config !== null && (() => {
    if (error) return true;
    try { return JSON.stringify(JSON.parse(draft)) !== JSON.stringify(JSON.parse(config.currentValue)); }
    catch { return true; }
  })();
  useReportDirty(CARD_ID, dirty);

  const updateRow = (rowId: number, changes: Partial<CustomParameterDefinitionRow>) => {
    setRows(current => current.map(row => row.rowId === rowId ? { ...row, ...changes } : row));
  };
  const changeView = (next: 'form' | 'json') => {
    if (next === view) return;
    // 无效 key / 重复 key 无法无损编码成对象，修正后才能切换；JSON 草稿始终保留原文。
    if (error) { message.warning('请先修正当前编辑内容中的错误'); return; }
    if (next === 'json') setJsonDraft(definitionRowsToJson(formRows));
    else setRows(withRowIds(definitionsToRows(JSON.parse(jsonDraft) as CustomParameterDefinitions)));
    setView(next);
  };
  const handleSave = async () => {
    if (error || saving || resetting) return;
    setSaving(true);
    try {
      applyConfig(await updateJobCustomParametersConfig(draft));
      message.success('已保存，创建任务时可选用最新参数目录');
    } catch { message.error('保存任务自定义参数失败，请检查配置后重试'); }
    finally { setSaving(false); }
  };
  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetJobCustomParametersConfig());
      message.success('已恢复默认参数目录');
    } catch { message.error('恢复默认参数目录失败，请重试'); }
    finally { setResetting(false); }
  };

  const taskTypeOptions = taskTypes.map(type => ({
    value: type.id, label: `${type.name} (${type.id})${type.enableFlag === 0 ? ' · 已停用' : ''}`,
  }));
  for (const id of new Set(rows.flatMap(row => row.taskTypeIds))) {
    if (!taskTypeOptions.some(option => option.value === id)) taskTypeOptions.push({ value: id, label: `任务类型 ${id}（当前未返回）` });
  }

  return (
    <Panel id={CARD_ID} index={index} variant="cfg" kicker="JOB PARAMETERS" title="任务自定义参数" dirty={dirty}
      subtitle="配置创建任务时可选填的参数、输入类型和适用任务类型。模型参数使用完整发布版本名。"
      info={<div>参数 key 最多 64 位，只含小写字母、数字和下划线且以字母开头。最多 32 项。模型类型固定使用 key：model_release，会固定展示在创建页 Job 名称上方，不出现在自定义参数下拉里。任务类型留空表示全部适用。</div>}
      tools={<><Segmented size="small" value={view} options={[{ value: 'form', label: '表单' }, { value: 'json', label: 'JSON' }]}
        onChange={value => changeView(value as 'form' | 'json')} disabled={loading || saving || resetting || !config} />
        <RefreshButton loading={loading} onClick={fetchConfig} /></>}>
      {loading && !config ? <PanelLoading /> : !config ? <PanelError onRetry={fetchConfig} /> : (
        <>
          <div className="sc-meta">{SOURCE_PILL[config.source]}<span className="sc-editor-hint">停用参数只影响后续创建，历史任务仍保留原参数。</span></div>
          <div className="sc-jcp-content">
            {view === 'json' ? (
              <JsonEditor value={jsonDraft} onChange={setJsonDraft} validate={(_, raw) => parameterDefinitionsJsonError(raw)}
                onSave={handleSave} minRows={12} maxRows={28} readOnly={saving || resetting} />
            ) : (
              <>
                {taskTypesError && <Alert showIcon type="warning" message="任务类型列表加载失败，已有选择会保留。" action={
                  <Button size="small" onClick={() => setTaskTypesRevision(value => value + 1)}>重试</Button>
                } />}
                {rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无自定义参数，添加后即可在创建任务时选用" />}
                {rows.map(row => (
                  <div key={row.rowId} className="sc-jcp-row">
                    <div className="sc-jcp-fields">
                      <label>参数 key<Input value={row.key} maxLength={64} placeholder="例如 model_release" disabled={saving || resetting}
                        onChange={event => updateRow(row.rowId, { key: event.target.value })} /></label>
                      <label>显示名称<Input value={row.label} maxLength={128} placeholder="例如 模型发布版本" disabled={saving || resetting}
                        onChange={event => updateRow(row.rowId, { label: event.target.value })} /></label>
                      <label>输入类型<Select value={row.inputType} options={[...CUSTOM_PARAMETER_INPUT_TYPES]} disabled={saving || resetting}
                        onChange={inputType => updateRow(row.rowId, {
                          inputType,
                          ...(inputType === 'select' ? {} : { options: [] }),
                          ...(inputType === 'model_release' ? {} : { requiredInstallDirs: {} }),
                        })} /></label>
                      <label className="sc-jcp-enabled">启用<Switch checked={row.enabled} checkedChildren="启用" unCheckedChildren="停用" disabled={saving || resetting}
                        onChange={enabled => updateRow(row.rowId, { enabled })} /></label>
                      <label className="sc-jcp-wide">说明<Input.TextArea value={row.description} maxLength={1024} autoSize={{ minRows: 1, maxRows: 3 }}
                        placeholder="向用户说明此参数的作用" disabled={saving || resetting}
                        onChange={event => updateRow(row.rowId, { description: event.target.value })} /></label>
                      {row.inputType === 'select' && (
                        <label className="sc-jcp-wide">选择项<Select mode="tags" value={row.options ?? []} open={false}
                          placeholder="输入选项文本后回车添加，可用逗号分隔批量粘贴" tokenSeparators={[',']} disabled={saving || resetting}
                          onChange={options => updateRow(row.rowId, { options })} /></label>
                      )}
                      {row.inputType === 'model_release' && (
                        <div className="sc-jcp-wide">
                          必需模型目录（接受 Job 时预检，在 JSON 视图的 requiredInstallDirs 中编辑）
                          <div>
                            {Object.keys(row.requiredInstallDirs ?? {}).length === 0 ? '未配置' : Object.entries(row.requiredInstallDirs ?? {}).map(
                              ([taskTypeId, directories]) => (
                                <div key={taskTypeId}>
                                  {taskTypeOptions.find(option => String(option.value) === taskTypeId)?.label ?? `任务类型 ${taskTypeId}`}：{directories.join('、')}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      <label className="sc-jcp-wide">适用任务类型<Select mode="multiple" value={row.taskTypeIds} options={taskTypeOptions} loading={taskTypesLoading}
                        showSearch optionFilterProp="label" placeholder="全部任务类型" allowClear disabled={saving || resetting}
                        onChange={taskTypeIds => updateRow(row.rowId, { taskTypeIds })} /></label>
                    </div>
                    <Button type="text" danger icon={<DeleteOutlined />} aria-label={`删除参数 ${row.key || '未命名'}`} title="删除参数" disabled={saving || resetting}
                      onClick={() => setRows(current => current.filter(item => item.rowId !== row.rowId))} />
                  </div>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} disabled={rows.length >= MAX_CUSTOM_PARAMETERS || saving || resetting}
                  onClick={() => setRows(current => [...current, {
                    rowId: ++nextRowId, key: '', label: '', description: '', inputType: 'text', enabled: true, taskTypeIds: [], options: [],
                  }])}>新增参数</Button>
              </>
            )}
          </div>
          <div className="sc-editor">
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirty || !!error || resetting || loading} onClick={handleSave}>保存</Button>
            <Button size="small" icon={<UndoOutlined />} title="撤销本地改动" aria-label="撤销任务参数改动" disabled={!dirty || saving || resetting}
              onClick={() => loadDraft(config.currentValue)} />
            <Popconfirm title="恢复默认任务参数目录？" description="当前参数目录将被默认配置替换，历史任务不受影响。" okText="恢复默认" cancelText="取消" onConfirm={handleReset}>
              <Button size="small" danger icon={<RollbackOutlined />} loading={resetting} disabled={saving || loading}>恢复默认</Button>
            </Popconfirm>
            {error && <span className="sc-editor-hint bad" role="alert">{error}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}
